import classNames from "classnames";
import { ReactNode } from "react";
import {
  BANK_ACCOUNT_STATUS,
  BANK_CONNECTION_TYPE,
} from "constants/dataSource";
import { BankConnection } from "types/Models/banks";
import { pluralize } from "utils/pluralize";
import { ConnectionCard, getConnectionInfoItem } from "../DataSourcesList";
import { CloudUploadSmall } from "components/icons/CloudUploadSmall";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { useBankConnect } from "hooks/useBankConnect";
import { useModal } from "hooks/useModal";
import { ManualToPlaidToDirectMigration } from "components/ManualToPlaidToDirectMigration/ManualToPlaidToDirectMigration";
import { Button } from "components/DesignSystem/Button/Button";
import { PlaidManageAccounts } from "./PlaidManageAccounts";
import { stopPropagation } from "utils/stopPropagation";
import { ReConnect } from "components/Connections/ReConnect";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";
import { getDisconnectedMessage } from "utils/bankNameAcc";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";

export const BankConnectionCTA = ({
  connection,
  onConnect,
  isConnectionLoading,
  onSuccess,
}: {
  connection: BankConnection;
  onConnect: ReturnType<typeof useBankConnect>["onConnect"];
  isConnectionLoading: boolean;
  onSuccess?: () => void;
}) => {
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.DATA_SOURCES
  );
  const plaidUpdateModal = useModal();

  if (!hasWritePermission) return <></>;

  const migrationAvailable =
    // If connection is manual and plaid or direct is available we can migrate
    (connection.connection_type === "MANUAL" &&
      (connection.plaid_institution_id ||
        connection.direct_connection?.uuid)) ||
    // If connection is not available or update is available and direct connection is available we can migrate
    ((!connection.is_connection_available || connection.update_available) &&
      connection.direct_connection?.uuid) ||
    // If connection is plaid and direct connection is available we can migrate
    (connection.plaid_institution_id && connection.direct_connection?.uuid);

  if (migrationAvailable) {
    return (
      <ManualToPlaidToDirectMigration
        onSuccess={onSuccess}
        connection={connection}
      />
    );
  }

  if (
    (!connection.is_connection_available || connection.update_available) &&
    (!connection.direct_connection?.uuid || !connection.plaid_institution_id)
  ) {
    if (connection.update_available) {
      return (
        <div>
          <Button
            customType="primary"
            size="small"
            onClick={(e) => {
              if (document.body) {
                document.body.style.pointerEvents = "auto";
              }
              e.stopPropagation();
              plaidUpdateModal.open();
            }}
          >
            Update
          </Button>
          <div onClick={stopPropagation}>
            <PlaidManageAccounts
              isOpen={plaidUpdateModal.isOpen}
              close={plaidUpdateModal.close}
              connection={connection}
            />
          </div>
        </div>
      );
    }

    return (
      <ReConnect
        type={connection.update_available ? "UPDATE" : "RECONNECT"}
        isLoading={isConnectionLoading}
        onConnect={onConnect}
        connection={connection}
      />
    );
  }

  const newAccounts = connection.bank_accounts.filter(
    (account) => account.is_new_bank_account
  );

  if (newAccounts.length) {
    return (
      <Button customType="primary_outlined" size="small">
        View
      </Button>
    );
  }

  return <></>;
};

export const BankConnections = ({
  connection,
  onMigrationSuccess,
}: {
  connection: BankConnection;
  onMigrationSuccess: () => void;
}) => {
  const { url } = useRouteMatch();
  const { search } = useLocation();
  const history = useHistory();
  const { onConnect, isLoading: isConnectBankLoading } = useBankConnect();

  const {
    disconnectedStatus,
    hasLongerName,
    isReconciliationRequired,
    canReconcile,
    reconcilliationRequiredStatus,
    updatedAt,
    isAllAccountsClosed,
  } = getConnectionInfoItem({ connection });

  const newAccounts = connection.bank_accounts.filter(
    (account) => account.is_new_bank_account
  );

  const disconnectedMessage = getDisconnectedMessage(connection);

  const description = isAllAccountsClosed ? (
    <span className="t-text-red-70">No active accounts</span>
  ) : (
    <span
      className={classNames("", {
        "t-text-green-80": connection.is_connection_available,
        "t-text-red-70": !connection.is_connection_available,
      })}
    >
      {connection.is_connection_available
        ? `${pluralize(
            connection.bank_accounts.filter(
              ({ status }) => status === BANK_ACCOUNT_STATUS.ACTIVE
            ).length,
            "account",
            "accounts"
          )} connected`
        : disconnectedMessage}
    </span>
  );

  let cardDescription: ReactNode = <span>&nbsp;</span>;
  if (connection.connection_type !== BANK_CONNECTION_TYPE.MANUAL) {
    cardDescription = description;
  }

  return (
    <button
      key={connection.uuid}
      className="all:unset"
      onClick={() => history.push(`${url}/${connection.uuid}${search}`)}
    >
      <ConnectionCard
        banner={
          connection.is_connection_available &&
          (connection.update_available ? (
            <div className="t-absolute t-top-0 t-inset-x-0 t-flex t-justify-center t-bg-line-gradient-blue t-py-1 t-px-2 t-text-body-sm-lite t-text-blue-80 t-items-center t-gap-1">
              <span className="t-flex">
                <CloudUploadSmall color="currentColor" size="16" />
              </span>
              <span> New accounts found</span>
            </div>
          ) : (
            newAccounts.length > 0 && (
              <div className="t-absolute t-top-0 t-inset-x-0 t-flex t-justify-center t-bg-line-gradient-blue t-py-1 t-px-2 t-text-body-sm-lite t-text-blue-80 t-items-center t-gap-1">
                <span className="t-flex">
                  <CloudUploadSmall color="currentColor" size="16" />
                </span>
                <span>
                  {newAccounts.length}{" "}
                  {pluralize(
                    newAccounts.length,
                    "new account",
                    "new accounts",
                    false
                  )}{" "}
                  syncing
                </span>
              </div>
            )
          ))
        }
        status={reconcilliationRequiredStatus || disconnectedStatus}
        logoUrl={connection.logo_url}
        disconnected={
          !connection.is_connection_available || isReconciliationRequired
        }
        name={
          <div
            className={classNames(
              "t-flex t-gap-1 t-items-center t-text-center",
              {
                "t-flex-col": hasLongerName,
              }
            )}
          >
            <span
              className={classNames({
                "t-order-2": hasLongerName,
                "t-order-1": !hasLongerName,
              })}
            >
              {connection.name}
            </span>
          </div>
        }
        tag={
          <div>
            <span
              className={classNames({
                "t-order-1": hasLongerName,
                "t-order-2": !hasLongerName,
                "t-visible":
                  connection.connection_type === "MANUAL" ||
                  connection.connection_type === "PLAID",
                "t-invisible":
                  connection.connection_type !== "MANUAL" &&
                  connection.connection_type !== "PLAID",
              })}
            >
              <Tag size="small" rounded icon={false} tagType="light_grey">
                {connection.connection_type === "MANUAL"
                  ? "Manually added"
                  : connection.connection_type === "PLAID"
                  ? "Via plaid"
                  : "direct"}
              </Tag>
            </span>
          </div>
        }
        description={cardDescription}
        updatedAt={!canReconcile ? updatedAt : ""}
        total={
          isAllAccountsClosed
            ? undefined
            : connection.bank_accounts?.reduce((acc, account) => {
                return account.status === BANK_ACCOUNT_STATUS.CLOSED
                  ? acc
                  : acc + account.available_balance;
              }, 0)
        }
        CTA={
          isAllAccountsClosed ? undefined : (
            <div className="t-flex t-flex-col t-gap-1 t-justify-center">
              <BankConnectionCTA
                connection={connection}
                isConnectionLoading={isConnectBankLoading}
                onConnect={onConnect}
                onSuccess={onMigrationSuccess}
              />
            </div>
          )
        }
      />
    </button>
  );
};
